import { Link } from '@inertiajs/react'
import { useEffect } from 'react'
import PropTypes from 'prop-types'
import Button from '../ui/Button'

/**
 * Mobile menu overlay component
 * Slide-in navigation for mobile devices
 *
 * @component
 */
export default function MobileMenu({ isOpen, onClose, links, auth, isActive }) {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Menu Panel */}
      <div
        className="fixed top-0 right-0 bottom-0 w-80 max-w-full bg-white z-50 shadow-xl md:hidden overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-label="Menu mobile"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <span className="text-lg font-bold text-gray-900">Menu</span>
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Fermer le menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 px-4 py-6">
            <ul className="space-y-2">
              {links.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    onClick={onClose}
                    className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      isActive(link.href)
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Auth Actions */}
          <div className="p-4 border-t border-gray-200 space-y-2">
            {auth?.user ? (
              <>
                <Link href={auth.user.admin ? '/admin' : '/dashboard'} onClick={onClose}>
                  <Button variant="primary" className="w-full">
                    {auth.user.admin ? 'Administration' : 'Interface de gestion'}
                  </Button>
                </Link>
                <div className="px-4 py-2 text-sm text-gray-600">
                  {auth.user.email}
                </div>
                <Link href="/sign-out" method="delete" as="button" onClick={onClose}>
                  <Button variant="outline" className="w-full">
                    Déconnexion
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link href="/sign-in" onClick={onClose}>
                  <Button variant="outline" className="w-full">
                    Connexion
                  </Button>
                </Link>
                <Link href="/sign-up" onClick={onClose}>
                  <Button variant="primary" className="w-full">
                    Créer un compte
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

MobileMenu.propTypes = {
  /** Whether the menu is open */
  isOpen: PropTypes.bool.isRequired,
  /** Callback to close the menu */
  onClose: PropTypes.func.isRequired,
  /** Navigation links */
  links: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  /** Authentication state */
  auth: PropTypes.object,
  /** Function to check if a link is active */
  isActive: PropTypes.func.isRequired,
}
